// SPDX-License-Identifier: AGPL-3.0-or-later
import { z } from 'zod';
/**
 * FHIR R4 `Address` data type. Only the fields intakeform actively
 * consumes are validated; unknown FHIR fields round-trip via
 * `.passthrough()` so a future migration can add `period`, `extension`,
 * etc. without a breaking change here.
 *
 * Reference: https://www.hl7.org/fhir/R4/datatypes.html#Address
 */
export const FhirAddressUseSchema = z.enum(['home', 'work', 'temp', 'old', 'billing']);
export const FhirAddressTypeSchema = z.enum(['postal', 'physical', 'both']);
/**
 * Upper bound on `Address.line`. A stored address arrives as a patient's
 * free-text answer (`valueString`) and is parsed again in the clinician's
 * browser by `parseAddressString`, so it is untrusted input: zod records one
 * issue per failing array element, and from zod 4.6.6 a `.max()` stops that
 * at one issue (GHSA-mrhx-c7xc-m3w5). Our renderer writes at most two lines
 * (street, unit) and postal addresses rarely need more than four or five;
 * 20 leaves room for imported data. The schema has no other array and does
 * not nest, so this one cap bounds the whole parse.
 */
export const MAX_ADDRESS_LINES = 20;
export const FhirAddressSchema = z
    .object({
    use: FhirAddressUseSchema.optional(),
    type: FhirAddressTypeSchema.optional(),
    /** Free-text fallback when the structured fields aren't reliable. */
    text: z.string().optional(),
    /** Street + unit number, one entry per line. */
    line: z.array(z.string()).max(MAX_ADDRESS_LINES).optional(),
    city: z.string().optional(),
    /** District / county / borough. */
    district: z.string().optional(),
    /** State, province, territory, or canton. */
    state: z.string().optional(),
    postalCode: z.string().optional(),
    /** Country name; we also accept ISO 3166-1 alpha-2 / alpha-3 codes. */
    country: z.string().optional(),
})
    .passthrough();
/**
 * Parse a string payload (typically a JSON-serialised Address stored in
 * the QR answer's `valueString`) into a FhirAddress. Returns `undefined`
 * when the string isn't valid JSON or doesn't match the schema; callers
 * fall back to treating the raw string as `text`.
 */
export function parseAddressString(value) {
    const trimmed = value.trim();
    if (trimmed.length === 0)
        return undefined;
    let parsed;
    try {
        parsed = JSON.parse(trimmed);
    }
    catch {
        return undefined;
    }
    const result = FhirAddressSchema.safeParse(parsed);
    return result.success ? result.data : undefined;
}
/** Serialise an Address for storage in the QR answer's `valueString`. */
export function serializeAddress(address) {
    return JSON.stringify(address);
}
/**
 * Produce a one-line representation suitable for a single-line preview
 * (e.g. inside a dashboard cell). Joins line + city + state + postal +
 * country with commas, skipping empties.
 */
export function formatAddressOneLine(address) {
    const parts = [];
    if (address.line !== undefined)
        parts.push(...address.line.filter((l) => l.length > 0));
    if (address.city !== undefined && address.city.length > 0)
        parts.push(address.city);
    if (address.state !== undefined && address.state.length > 0)
        parts.push(address.state);
    if (address.postalCode !== undefined && address.postalCode.length > 0)
        parts.push(address.postalCode);
    if (address.country !== undefined && address.country.length > 0)
        parts.push(address.country);
    if (parts.length === 0 && address.text !== undefined)
        return address.text;
    return parts.join(', ');
}
//# sourceMappingURL=address.js.map